import {
    useEffect,
    useRef
} from "react";

interface FantasyModalBackGuardOptions {
    isOpen: boolean;
    onDismiss: () => void;
    priority?: number;
    canDismiss?: boolean;
}

export function useFantasyModalBackGuard({
    isOpen,
    onDismiss,
    priority = 10000,
    canDismiss = true
}: FantasyModalBackGuardOptions): void {
    const hasHistoryGuardRef = useRef(false);
    const onDismissRef = useRef(onDismiss);
    const canDismissRef = useRef(canDismiss);

    /*
        Evita recrear el listener y modificar el historial cada vez
        que el componente se vuelve a renderizar.
    */
    useEffect(
        function () {
            onDismissRef.current = onDismiss;
        },
        [onDismiss]
    );

    useEffect(
        function () {
            canDismissRef.current = canDismiss;
        },
        [canDismiss]
    );

    useEffect(
        function () {
            if (
                !isOpen ||
                typeof window === "undefined"
            ) {
                return;
            }

            /*
                Crea una entrada temporal en la misma ruta.
                El swipe back consume esta entrada y cierra el modal,
                sin regresar a la pantalla anterior.
            */
            window.history.pushState(
                {
                    ...(window.history.state ?? {}),
                    fantasyModalBackGuard: true
                },
                "",
                window.location.href
            );

            hasHistoryGuardRef.current = true;

            function handlePopState(): void {
                if (!hasHistoryGuardRef.current) {
                    return;
                }

                /*
                    Si el modal no puede cerrarse —por ejemplo,
                    durante el envío de un trade— se restaura la
                    protección de historial y se mantiene abierto.
                */
                if (!canDismissRef.current) {
                    window.history.pushState(
                        {
                            ...(window.history.state ?? {}),
                            fantasyModalBackGuard: true
                        },
                        "",
                        window.location.href
                    );

                    hasHistoryGuardRef.current = true;

                    return;
                }

                /*
                    El swipe ya consumió la entrada temporal.
                */
                hasHistoryGuardRef.current = false;

                onDismissRef.current();
            }

            function handleIonBackButton(
                nativeEvent: Event
            ): void {
                const event = nativeEvent as CustomEvent<{
                    register: (
                        priority: number,
                        handler: () => void
                    ) => void;
                }>;


                event.detail.register(
                    priority,
                    function (): void {
                        /*
                            Se consume el Back antes de que
                            Ionic Router navegue a la ruta previa.
                        */
                        if (!canDismissRef.current) {
                            return;
                        }

                        onDismissRef.current();
                    }
                );
            }

            window.addEventListener(
                "popstate",
                handlePopState
            );

            document.addEventListener(
                "ionBackButton",
                handleIonBackButton
            );

            return function (): void {
                window.removeEventListener(
                    "popstate",
                    handlePopState
                );

                document.removeEventListener(
                    "ionBackButton",
                    handleIonBackButton
                );

                /*
                    Si se cerró con botón, backdrop o acción interna,
                    elimina la entrada temporal.

                    Si se cerró por swipe back, el popstate ya la
                    consumió y hasHistoryGuardRef es false.
                */
                if (hasHistoryGuardRef.current) {
                    hasHistoryGuardRef.current = false;

                    window.history.back();
                }
            };
        },
        [
            isOpen,
            priority
        ]
    );
}
